"use client"

import { useMemo, useState } from "react"
import { ArrowUpRight, Info, RefreshCw } from "lucide-react"
import {
  StatBorderGrid,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import { TOOL_METADATA } from "@/lib/tool-metadata"
import { readCount } from "@/lib/tools/numeric-input"

const FALLBACK_RELATED = [
  "freezer-storage-capacity-calculator",
  "freezer-meat-storage-calculator",
  "freezer-vacuum-sealer-calculator",
  "freezer-energy-cost-calculator",
]

function buildRelated(slugs: string[], currentSlug: string) {
  const seen = new Set<string>([currentSlug])
  const out: { title: string; category: string; description: string; href: string; time: string }[] = []
  for (const s of [...slugs, ...FALLBACK_RELATED]) {
    if (out.length === 4) break
    if (seen.has(s)) continue
    const m = TOOL_METADATA[s]
    if (!m) continue
    seen.add(s)
    out.push({
      title: m.name,
      category: m.category,
      description: m.shortDescription,
      href: `/tools/${s}`,
      time: "~30 sec",
    })
  }
  return out
}

const META = {
  name: "Upright Freezer Shelf Planner",
  category: "Calculator" as const,
  mediumDescription:
    "Get a shelf-by-shelf organization plan for your upright freezer based on shelf count, door storage, and how you use it. Built around USDA FIFO rotation and ENERGY STAR's 75%-full guidance.",
  faqs: [
    { question: "How should I organize an upright freezer?", answer: "Most-used items at eye level, heavy bulk on middle shelves, long-term storage at the bottom, small frequently-grabbed items in door bins. Group by category and use clear bins to keep zones intact." },
    { question: "What's the best temperature for an upright freezer?", answer: "USDA recommends 0°F (-18°C). Colder doesn't extend safety; warmer compromises long-term quality." },
    { question: "Should I keep my freezer 75% full?", answer: "Yes — ENERGY STAR recommends keeping freezers around 75% full. Frozen food acts as thermal mass and helps the unit hold temperature between compressor cycles." },
    { question: "What is FIFO rotation in a freezer?", answer: "First In, First Out — when you add new items, push older packages to the front so they get used first. This is the same rotation method commercial freezers use to prevent quality loss." },
    { question: "What goes in the bottom shelf or drawer?", answer: "Long-term bulk storage: whole turkeys, large roasts, vacuum-sealed bulk meat, ice packs. Things you won't reach for daily — the bottom is the hardest spot to access." },
    { question: "How do I keep cold air from spilling out of an upright?", answer: "Organize so a single door opening grabs everything for one meal. Group items by category so you're not hunting around with the door wide open. Door bins for the most frequently grabbed items." },
  ],
  relatedTools: ["freezer-storage-capacity-calculator", "freezer-meat-storage-calculator", "freezer-vacuum-sealer-calculator"],
}

interface ShelfZone {
  shelf: string
  height: string
  bestFor: string[]
}

export function UprightFreezerShelfPlannerPageClient() {
  const [numShelves, setNumShelves] = useState("5")
  const [hasDoor, setHasDoor] = useState("yes")
  const [usage, setUsage] = useState("general")

  // Original calculation logic preserved verbatim from the legacy page-client.
  const result = useMemo(() => {
    // An upright freezer has at least one shelf, and the largest residential
    // uprights ship with seven or eight — 12 is a generous ceiling. FRIDGE-384.
    const shelves = readCount(numShelves, { min: 1, max: 12 })
    if (shelves === null) return null

    const shelfPlan: ShelfZone[] = []

    shelfPlan.push({
      shelf: "Top shelf",
      height: "Eye level or above",
      bestFor: ["Frequently accessed items", "Ice cream and frozen desserts", "Frozen vegetables (small bags)", "Bread and baked goods"],
    })

    if (shelves >= 4) {
      shelfPlan.push({
        shelf: "Upper-middle shelf",
        height: "Chest to eye level",
        bestFor: ["Chicken breasts and portions", "Ground meat (1–2 lb packages)", "Fish fillets", "Frozen meals"],
      })
    }

    shelfPlan.push({
      shelf: "Middle shelf",
      height: "Waist to chest level",
      bestFor: ["Bulk meat purchases", "Large frozen vegetable bags", "Meal prep batches", "Family-sized entrees"],
    })

    if (shelves >= 4) {
      shelfPlan.push({
        shelf: "Lower-middle shelf",
        height: "Below waist level",
        bestFor: ["Large cuts of meat", "Whole chickens", "Bulk purchases", "Stock and broth containers"],
      })
    }

    shelfPlan.push({
      shelf: "Bottom shelf / drawer",
      height: "Floor level",
      bestFor: ["Whole turkeys", "Large roasts", "Vacuum-sealed bulk meat", "Ice packs", "Long-term storage"],
    })

    const doorStorage =
      hasDoor === "yes"
        ? [
            { section: "Top door bins", items: ["Butter and margarine", "Small juice concentrates", "Ice cube trays", "Popsicles"] },
            { section: "Middle door bins", items: ["Frozen vegetables (small bags)", "Frozen herbs", "Sauces and gravies"] },
            { section: "Bottom door bins", items: ["Larger frozen items", "Frozen juice containers", "Ice packs"] },
          ]
        : []

    return { shelfPlan, doorStorage, usage }
  }, [numShelves, hasDoor, usage])

  return (
    <ToolPageShell
      toolName={META.name}
      heroEyebrow={META.category}
      heroTitlePre="A real plan for"
      heroTitleItalic="every shelf"
      heroTitlePost="of your upright freezer."
      heroSubtitle={META.mediumDescription}
      heroMeta={["⏱ ~20 sec", "USDA FIFO + ENERGY STAR 75%"]}
      heroShare={<ToolShareBar title={META.name} />}
      heroRight={
        <StatBorderGrid
          stats={[
            { value: "0°F", label: "USDA target", note: "Long-term storage" },
            { value: "75%", label: "Target fullness", note: "ENERGY STAR" },
            { value: "FIFO", label: "Rotation", note: "Old to front" },
            { value: "Eye lvl", label: "Most used", note: "Top shelf" },
          ]}
          className="w-full max-w-md md:max-w-none md:w-auto mx-auto md:mx-0"
        />
      }
      faqs={META.faqs}
      relatedTools={buildRelated(META.relatedTools, "upright-freezer-shelf-planner")}
      cta={{
        title: "Upgrade to an upright freezer with smarter shelving?",
        body: "Browse upright freezers — sliding bins, full-extension drawers, adjustable shelves.",
        buttonLabel: "Browse upright freezers",
        buttonHref: "/collections/upright-freezers",
      }}
    >
      <section className="bg-gray-50 py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 space-y-6">
          <div className="grid lg:grid-cols-2 gap-6 lg:items-stretch">
            <div className="bg-white rounded-xl p-6 md:p-7 border border-gray-900 flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-[12px] font-bold tracking-[0.15em] text-[#ff9951]">STEP 01</span>
                <div className="flex-1 h-[1px] bg-gray-300" />
              </div>
              <h2 className="text-gray-900 font-black" style={{ fontSize: 28, letterSpacing: "-0.025em", lineHeight: 1.05 }}>
                Shelves.
                <br />
                Door bins. Use.
              </h2>
              <p className="text-[14px] text-gray-600 mt-2 leading-[1.5]">
                Most-used items at eye level. Bulk in the middle. Long-term storage at the bottom.
              </p>

              <div className="mt-7 space-y-5">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Number of shelves
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min={1}
                      max={12}
                      step="1"
                      value={numShelves}
                      onChange={(e) => setNumShelves(e.target.value)}
                      className="tool-input-clean w-full px-4 py-4 text-[22px] font-bold rounded-lg outline-none transition-colors border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[13px] font-mono text-gray-500">shelves</span>
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Door storage
                  </label>
                  <select
                    value={hasDoor}
                    onChange={(e) => setHasDoor(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="yes">Yes — door bins available</option>
                    <option value="no">No — solid door</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Primary use
                  </label>
                  <select
                    value={usage}
                    onChange={(e) => setUsage(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="general">General household</option>
                    <option value="meal-prep">Meal prep</option>
                    <option value="bulk">Bulk storage</option>
                  </select>
                  <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> Bulk users should dedicate the bottom drawer to long-term storage.
                  </div>
                </div>

                <div className="pt-2 text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold flex items-center gap-1.5">
                  <RefreshCw className="w-3 h-3" /> Updates live as you type
                </div>
              </div>
            </div>

            <div className="flex flex-col">
              <div className="rounded-2xl p-6 md:p-8 relative overflow-hidden text-white flex-1" style={{ background: "linear-gradient(135deg, #0469f0 0%, #0053c4 100%)" }}>
                <div aria-hidden className="absolute top-0 right-0 w-[340px] h-[340px] rounded-full pointer-events-none" style={{ background: "rgba(255,153,81,0.35)", filter: "blur(60px)", transform: "translate(25%, -35%)" }} />
                <div className="relative">
                  <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/70 mb-6">
                    Your shelf plan
                  </div>
                  <div className="space-y-3">
                    {result?.shelfPlan.map((zone) => (
                      <div key={zone.shelf} className="rounded-lg p-3 bg-white/10 border border-white/15">
                        <div className="flex items-baseline justify-between gap-3">
                          <div className="text-[15px] font-black tracking-tight">{zone.shelf}</div>
                          <div className="text-[10px] uppercase tracking-[0.12em] text-white/60 font-mono">{zone.height}</div>
                        </div>
                        <div className="mt-1 text-[12px] text-white/80 leading-[1.45]">
                          {zone.bestFor.slice(0, 2).join(" · ")}
                        </div>
                      </div>
                    )) ?? <div className="text-[14px] text-white/70">Enter shelf details to see the plan.</div>}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {result && result.doorStorage.length > 0 && (
            <div className="bg-white rounded-xl p-6 md:p-7 border border-gray-200">
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#ff9951] mb-3">
                Door bin assignments
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                {result.doorStorage.map((d) => (
                  <div key={d.section}>
                    <div className="text-[13px] font-bold text-gray-900 mb-1">{d.section}</div>
                    <ul className="text-[12px] text-gray-600 space-y-1">
                      {d.items.map((i) => (
                        <li key={i}>• {i}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Step 02 · Pick the unit"
          title="Upright freezers with adjustable shelving"
          description="Adjustable wire shelves and basket drawers make the eye-level / bulk / bottom system work."
          collectionType="upright-freezer"
          fallbackCollectionType="bottom-freezer"
        />
      </section>

      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-[1fr_1.3fr] gap-8 md:gap-12 items-start">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">Under the hood</div>
              <h2 className="text-gray-900 font-black" style={{ fontSize: "clamp(24px, 3vw, 36px)", letterSpacing: "-0.03em", lineHeight: 1.05 }}>
                FIFO + 75%, <em className="italic text-[#ff9951]">always.</em>
              </h2>
              <p className="text-[14px] text-gray-600 mt-4 max-w-[400px] leading-[1.55]">
                Organization principles drawn from USDA FIFO rotation guidance and ENERGY STAR fullness recommendations for residential freezers.
              </p>
            </div>
            <ol className="list-none p-0 m-0 border-t border-gray-900">
              {[
                { n: "01", t: "Eye-level priority", d: "The top shelf gets the most-used items: ice cream, breakfast items, frequently grabbed packages. Less time with the door open = less cold lost." },
                { n: "02", t: "Bulk in the middle", d: "Heavy bulk packages (large meat cuts, batched meal prep) go waist-to-chest level. Easier on your back and visible enough to actually rotate." },
                { n: "03", t: "Bottom for long-term", d: "Whole turkeys, vacuum-sealed bulk meat, ice packs. Hardest to reach so reserve it for things you'll defrost weeks or months out." },
                { n: "04", t: "Door bins for grabs", d: "The warmest part of any freezer — never store fragile or long-term items here. Reserve door bins for butter, popsicles, frequently used small items." },
              ].map((s) => (
                <li key={s.n} className="grid grid-cols-[auto_1fr_auto] gap-4 sm:gap-7 items-center py-5 sm:py-6 border-b border-gray-900">
                  <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] text-[#ff9951]">{s.n}</div>
                  <div>
                    <h3 className="text-gray-900" style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}>{s.t}</h3>
                    <p className="mt-1.5 text-[14px] text-gray-600 leading-[1.55]">{s.d}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="Bottom-freezer fridges with the same shelf logic"
          description="Eye-level fridge access, freezer drawer at the bottom — uses the same most-used / least-used vertical hierarchy."
          collectionType="bottom-freezer"
          fallbackCollectionType="upright-freezer"
        />
      </section>
    </ToolPageShell>
  )
}
